import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../api/client';
import type { CostSnapshot } from '../api/types';

/**
 * Cost snapshot — backed by `services/monitor/handler.py` `cost_snapshot`
 * which wraps AWS Cost Explorer (per-tenant `tenant_id` tag filter).
 *
 * `params` selects the window: `{ days: '30' }`, `{ days: '90' }`, or a custom
 * `{ start, end }` (ISO, end-exclusive) — see the cost route's range filter.
 */

export type CostParams = Record<string, string>;

export const costKeys = {
  snapshot: (params: CostParams = {}) => ['cost', 'snapshot', params] as const,
};

export function useCostSnapshot(params: CostParams = {}) {
  const qs = new URLSearchParams(params).toString();
  return useQuery<CostSnapshot>({
    queryKey: costKeys.snapshot(params),
    staleTime: 5 * 60_000,
    queryFn: async ({ signal }) =>
      apiFetch<CostSnapshot>(`/monitor/cost${qs ? `?${qs}` : ''}`, { method: 'GET', signal }),
  });
}

export interface OrgCost {
  id: string;
  name: string;
  billedMtd: number;
  runningHourly: number;
  runningAccrued: number;
  runningInstances: number;
}
export interface OrgCostsResponse {
  items: OrgCost[];
  totals: { billedMtd: number; runningHourly: number; runningAccrued: number };
  note: string;
}

/** Superadmin per-org cost: billed MTD (Cost Explorer) + live run-rate. */
export function useOrgCosts() {
  return useQuery<OrgCostsResponse>({
    queryKey: ['cost', 'orgs'],
    staleTime: 60_000,
    queryFn: async ({ signal }) => apiFetch<OrgCostsResponse>('/cost/orgs', { method: 'GET', signal }),
  });
}
